import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import LoadingButton from '@mui/lab/LoadingButton'
import ErrorIcon from '@mui/icons-material/Error'
import { DialogTitle } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'

import useDialog from '../useDialog'

interface UseDeleteConfirmDialogProps {
  title?: string
  content?: string
  onConfirm?: () => Promise<any>
  confirmTxt?: string
}

const useDeleteConfirmDialog = ({
  title,
  content,
  onConfirm,
  confirmTxt,
}: UseDeleteConfirmDialogProps) => {
  const { t } = useTranslation('common')
  const { render, closeDialog, ...rest } = useDialog()
  const [isLoading, setIsLoading] = useState(false)

  const handleClickConfirm = () => {
    setIsLoading(true)
    onConfirm?.()
      .then(() => closeDialog())
      // A rejected onConfirm keeps the dialog open; the caller has already
      // reported the error, so don't leave an unhandled rejection behind.
      .catch(() => undefined)
      .finally(() => setIsLoading(false))
  }

  return {
    dialog: render(
      <Box>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ErrorIcon sx={{ color: '#F59E1B' }} />
          <Typography variant="h5">{title || t('delete.confirmation')}</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>{content || t('delete.content')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDialog} disabled={isLoading}>
            {t('cancel')}
          </Button>
          <LoadingButton variant="contained" onClick={handleClickConfirm} loading={isLoading}>
            {confirmTxt || t('confirm')}
          </LoadingButton>
        </DialogActions>
      </Box>
    ),
    closeDialog,
    ...rest,
  }
}

export default useDeleteConfirmDialog
